import React from "react";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";

const PremiumDatePicker = ({ selectedDate, onSelect, darkMode }) => {
  const [currentViewDate, setCurrentViewDate] = React.useState(new Date());

  // Get start of today (local time) for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const year = currentViewDate.getFullYear();
  const month = currentViewDate.getMonth();

  // Logic for the calendar grid
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const monthName = currentViewDate.toLocaleString("default", { month: "long" });

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  const isSelected = (day) => {
    if (!selectedDate) return false;
    // Create date object from selectedDate and compare
    const d = new Date(selectedDate.replace(/-/g, '\/')); // Replace for cross-browser safety
    return (
      d.getDate() === day &&
      d.getMonth() === month &&
      d.getFullYear() === year
    );
  };

  const isToday = (day) => {
    return (
      today.getDate() === day &&
      today.getMonth() === month &&
      today.getFullYear() === year
    );
  };

  const isPast = (day) => {
    const dateToCheck = new Date(year, month, day);
    return dateToCheck < today;
  };

  // ✅ FIXED: Safer month navigation
  const changeMonth = (offset) => {
    const newDate = new Date(year, month, 1); // Set to 1st to avoid month-skipping bugs
    newDate.setMonth(newDate.getMonth() + offset);
    setCurrentViewDate(newDate);
  };

  return (
    <div
      className={`p-6 rounded-[2.5rem] shadow-2xl border max-w-md mx-auto transition-colors duration-300 ${darkMode
        ? "bg-slate-900 border-white/10 text-white"
        : "bg-white border-slate-100 text-slate-900"
        }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => changeMonth(-1)}
          className={`p-2 rounded-full transition ${darkMode ? "hover:bg-white/10" : "hover:bg-slate-100"
            }`}
        >
          <ArrowLeft size={20} />
        </button>

        <div className="flex items-center gap-2">
          <Calendar className="text-purple-400" size={20} />
          <h3 className="font-black text-xl tracking-tight">
            {monthName} {year}
          </h3>
        </div>

        <button
          onClick={() => changeMonth(1)}
          className={`p-2 rounded-full transition ${darkMode ? "hover:bg-white/10" : "hover:bg-slate-100"
            }`}
        >
          <ArrowRight size={20} />
        </button>
      </div>

      {/* Weekdays */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            className="text-center text-[11px] font-black uppercase opacity-40"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Dates Grid */}
      <div className="grid grid-cols-7 gap-2">
        {blanks.map((i) => (
          <div key={`blank-${i}`} />
        ))}

        {days.map((day) => {
          const disabled = isPast(day);
          const selected = isSelected(day);
          const isCurrentDay = isToday(day);

          return (
            <button
              key={`${year}-${month}-${day}`}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                // Format as YYYY-MM-DD
                const formattedDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                onSelect(formattedDate);
              }}
              className={`aspect-square rounded-2xl flex items-center justify-center font-bold text-sm transition-all
                ${selected
                  ? "bg-purple-400 text-slate-900 scale-110 shadow-lg shadow-purple-400/20"
                  : isCurrentDay
                    ? "border-2 border-purple-400 text-purple-500"
                    : disabled
                      ? "opacity-20 cursor-not-allowed"
                      : darkMode
                        ? "hover:bg-slate-800"
                        : "hover:bg-slate-100"
                }`}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Selected Date Preview */}
      {/* {selectedDate && (
        <div className={`mt-8 p-4 rounded-2xl border text-center ${
          darkMode ? "bg-purple-400/5 border-purple-400/20" : "bg-purple-50 border-purple-100"
        }`}>
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">
            Confirmed Date
          </p>
          <p className="text-lg font-black text-purple-500">
            {new Date(selectedDate.replace(/-/g, '\/')).toLocaleDateString(undefined, {
                weekday: 'short',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })}
          </p>
        </div>
      )} */}
    </div>
  );
};

export default PremiumDatePicker;